import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    styled,
    Box,
    ListItemText,
} from '@mui/material';

import { useMemo, useState } from 'react';
import { useMiniSidebar } from '../context/MiniSidebarContext';
import Sitemap from '../icons/sitemap.svg';
import UserCircleIcon from '../icons/user-circle.svg';
import {
    useAppConfigState,
    useAppGlobalDispatch,
    useLogOut,
    useVrsTranslationState,
} from '../context/AppContext/AppContext';
import {
    myVJBlack950,
    myVJBlack800,
    myVJBlack700,
    myVJBlue200,
    myVJWhite
} from '../constants/brandingColors';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useBanners } from '../hooks/useBanner';

const MobileDrawerPaper = styled('div')(() => ({
    width: 320,
    backgroundColor: myVJBlack950,
    color: myVJWhite,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
}));

const MobileNavigationContainer = styled('div')({
    padding: 0,
    margin: 0,
});

const MobileListItemButton = styled(ListItemButton)(() => ({
    padding: '16px 32px',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    backgroundColor: myVJBlack950,
    margin: 0,
    borderRadius: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
        backgroundColor: myVJBlue200,
        color: myVJWhite,
    },
    '&.Mui-selected': {
        backgroundColor: myVJBlue200,
        color: myVJWhite,
        '&:hover': {
            backgroundColor: myVJBlue200,
        },
    },
}));

/**
 * Simple menu item for user actions (Profile, About, Logout)
 */
const SimpleMenuItem = ({ title, onClick }) => (
    <ListItem disablePadding sx={{
        margin: 0,
        padding: '0 !important',
        '&.MuiListItem-root': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important'
        }
    }}>
        <ListItemButton
            onClick={onClick}
            aria-label={`${title} action`} // Add aria-label for user actions
            role="menuitem"
            sx={{
                padding: '16px 32px 16px 72px',
                backgroundColor: myVJBlack800,
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px',
                width: '100%',
                margin: 0,
                borderRadius: 0,
                '&:hover': {
                    backgroundColor: myVJBlue200,
                    color: myVJWhite,
                },
            }}
        >
            <ListItemText
                primary={title}
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'inherit'
                    }
                }}
            />
        </ListItemButton>
    </ListItem>
);

const MenuItem = ({
    item,
    level = 1,
    onNavigate,
    onClose,
    expandedItems = {},
    onToggleExpand,
}) => {
    const [localThirdLevelExpanded, setLocalThirdLevelExpanded] = useState({});
    const hasChildren = item.children && item.children.length > 0;

    const isExpanded = level >= 2
        ? localThirdLevelExpanded[item.segment] || false
        : expandedItems[item.segment] || false;

    const getButtonStyles = () => ({
        // Level 1: Main navigation items (16px padding, darkest background)
        // Level 2: Sub-menu items (24px left padding, medium background) 
        // Level 3: Sub-sub-menu items (32px left padding, lightest background)
        padding: level === 1 ? '16px 32px' : level === 2 ? '16px 32px' : '16px 32px 16px 72px',
        backgroundColor: level === 1 ? myVJBlack950 : level === 2 ? myVJBlack800 : myVJBlack700,
        fontSize: level === 1 ? '16px' : '16px',
        fontWeight: level === 1 ? 400 : 400,
        width: '100%',
        margin: 0,
        borderRadius: 0,
    });

    const handleClick = () => {
        if (hasChildren) {
            if (level >= 2) {
                setLocalThirdLevelExpanded(prev => ({
                    ...prev,
                    [item.segment]: !prev[item.segment]
                }));
            } else {
                onToggleExpand(item.segment);
            }
        } else {
            onNavigate(item.fullPath);
            if (onClose) onClose();
        }
    };

    const handleChildToggleExpand = (childSegment) => {
        setLocalThirdLevelExpanded(prev => ({
            ...prev,
            [childSegment]: !prev[childSegment]
        }));
    };

    // Generate aria-label based on level and functionality
    const getAriaLabel = () => {
        if (hasChildren) {
            const levelText = level === 1 ? 'main menu' : level === 2 ? 'submenu' : 'sub-submenu';
            const expandedState = isExpanded ? 'expanded' : 'collapsed';
            return `${item.title} ${levelText}, ${expandedState}. Click to ${isExpanded ? 'collapse' : 'expand'}.`;
        } else {
            const levelText = level === 1 ? 'main menu item' : level === 2 ? 'submenu item' : 'sub-submenu item';
            return `Navigate to ${item.title} ${levelText}`;
        }
    };

    return (
        <>
            <ListItem disablePadding sx={{
                margin: 0,
                padding: '0 !important',
                '&.MuiListItem-root': {
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important'
                }
            }}>
                <ListItemButton
                    onClick={handleClick}
                    disabled={item.disabled}
                    aria-label={getAriaLabel()} // Add aria-label here
                    aria-expanded={hasChildren ? isExpanded : undefined} // Add aria-expanded for expandable items
                    role={hasChildren ? 'button' : 'menuitem'} // Appropriate role
                    sx={{
                        ...getButtonStyles(),
                        color: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                            backgroundColor: myVJBlue200,
                            color: myVJWhite,
                        },
                        '&.Mui-selected': {
                            backgroundColor: myVJBlue200,
                            color: myVJWhite,
                        },
                        '&.Mui-disabled': {
                            opacity: 0.5,
                            cursor: 'not-allowed',
                        }
                    }}
                >
                    {level === 1 && (
                        <ListItemIcon sx={{
                            color: 'inherit',
                            minWidth: 40,
                            margin: 0,
                            padding: 0
                        }}>
                            {item.icon}
                        </ListItemIcon>
                    )}
                    {level === 2 && (
                        <Box sx={{
                            color: 'inherit',
                            minWidth: 40,
                            margin: 0,
                            padding: 0
                        }} />
                    )}

                    <ListItemText
                        primary={item.title}
                        sx={{
                            '& .MuiTypography-root': {
                                fontSize: level === 1 ? '16px' : '16px',
                                fontWeight: level === 1 ? 400 : 400,
                                color: 'inherit'
                            }
                        }}
                    />

                    {hasChildren && (
                        <ChevronRightIcon
                            sx={{
                                transform: isExpanded ? 'rotate(-90deg)' : 'rotate(90deg)', // Changed rotation values
                                transition: 'transform 0.2s',
                                fontSize: '20px',
                                opacity: 0.8,
                                color: 'rgba(255, 255, 255, 0.8)',
                            }}
                            aria-hidden="true"
                        />
                    )}
                </ListItemButton>
            </ListItem>


            {hasChildren && isExpanded && (
                <div
                    role="menu" // Add role for the submenu container
                    aria-label={`${item.title} submenu`} // Add aria-label for the submenu container
                >
                    {item.children.map((child) => (
                        <MenuItem
                            key={child.segment || child.title}
                            item={child}
                            level={level + 1}
                            onNavigate={onNavigate}
                            onClose={onClose}
                            expandedItems={level >= 1 ? localThirdLevelExpanded : expandedItems}
                            onToggleExpand={handleChildToggleExpand}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

const MobileNavigationItem = ({
    item,
    onNavigate,
    expandedItems = {},
    onToggleExpand
}) => {
    const { closeMobile } = useMiniSidebar();

    return (
        <MenuItem
            item={item}
            level={1}
            onNavigate={onNavigate}
            onClose={closeMobile}
            expandedItems={expandedItems}
            onToggleExpand={onToggleExpand}
        />
    );
};

const MobileUserProfileSection = ({
    hasAccessVrs,
    setShowEditProfileDialog,
    setShowAboutDialog,
    logOut,
    userDispatch,
    expanded,
    onToggleExpand
}) => {
    const { _T } = useVrsTranslationState();
    const userMenuItems = [
        ...(hasAccessVrs('edit-profile') ? [{
            title: _T('Profile'),
            action: () => setShowEditProfileDialog(true)
        }] : []),
        {
            title: _T('About'),
            action: () => setShowAboutDialog(true)
        },
        {
            title: _T('Sign out'),
            action: () => logOut(userDispatch)
        }
    ];

    return (
        <>
            <ListItem disablePadding sx={{
                margin: 0,
                padding: '0 !important',
                '&.MuiListItem-root': {
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important'
                }
            }}>
                <MobileListItemButton
                    onClick={onToggleExpand}
                    aria-label={`User profile menu, ${expanded ? 'expanded' : 'collapsed'}. Click to ${expanded ? 'collapse' : 'expand'}.`}
                    aria-expanded={expanded}
                    role="button"
                >
                    <ListItemIcon sx={{
                        color: 'inherit',
                        minWidth: 40,
                        margin: 0,
                        padding: 0
                    }}>
                        <img src={UserCircleIcon} alt="User Icon" style={{ width: 24, height: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={_T("My account")}
                        sx={{
                            '& .MuiTypography-root': {
                                fontSize: '16px',
                                fontWeight: 400,
                                color: 'inherit'
                            }
                        }} />
                    <ChevronRightIcon
                        sx={{
                            transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)', // Changed rotation values
                            transition: 'transform 0.2s',
                            fontSize: '16px',
                            color: 'rgba(255, 255, 255, 0.7)'
                        }}
                        aria-hidden="true"
                    />
                </MobileListItemButton>
            </ListItem>

            {expanded && (
                <div
                    role="menu"
                    aria-label="User profile actions menu"
                >
                    {userMenuItems.map((item, index) => (
                        <SimpleMenuItem
                            key={index}
                            title={item.title}
                            onClick={() => {
                                item.action();
                                onToggleExpand();
                            }}
                        />
                    ))}
                </div>
            )}
        </>
    );
};


interface MobileSidebarProps {
    mobileOpen: boolean;
    closeMobile: () => void;
    navigationItems: any[];
    configurationItem: any;
    isPureDesignUser: boolean;
    userProfileLarge: any;
    userProfile: any;
    hasAccessVrs: (claim: string) => boolean;
    setShowEditProfileDialog: (show: boolean) => void;
    setShowAboutDialog: (show: boolean) => void;
    onNavigate: (path: string) => void;
}

export default function MobileSidebar({
    mobileOpen,
    closeMobile,
    navigationItems,
    configurationItem,
    isPureDesignUser,
    hasAccessVrs,
    setShowEditProfileDialog,
    setShowAboutDialog,
    onNavigate,
}: MobileSidebarProps) {
    const [mobileExpandedItems, setMobileExpandedItems] = useState({});
    const logOut = useLogOut();
    const userDispatch = useAppGlobalDispatch();
    const { headerTitleInfo } = useAppConfigState();
    const { _T } = useVrsTranslationState();
    const { getBannersByPriority } = useBanners();
    const sortedBanners = getBannersByPriority();

    const isBreadcrumbVisible = !!headerTitleInfo && headerTitleInfo.linkParams?.parents;
    const topPadding = useMemo(() => {
        let value = isBreadcrumbVisible ? 152 : 128;
        if (sortedBanners.length > 0) {
            value += (50 * sortedBanners.length) + 10;
        }
        return value;
    }, [isBreadcrumbVisible, sortedBanners]);


    const handleMobileToggleExpand = (segment) => {
        setMobileExpandedItems(prev => {
            const newState = {};
            if (!prev[segment]) {
                newState[segment] = true;
            }
            return newState;
        });
    };

    return (
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={closeMobile}
            anchor="right"
            ModalProps={{
                keepMounted: true,
            }}
            PaperProps={{
                component: MobileDrawerPaper,
                sx: {
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: myVJBlack950,
                }
            }}
            sx={{
                '& .MuiDrawer-paper': {
                    top: `${topPadding}px`,
                    paddingBottom: '110px',
                    height: `calc(100vh - ${topPadding}px)`,
                    overflowY: 'auto',
                    right: 0,
                    left: 'auto',
                    backgroundColor: myVJBlack950,
                }
            }}
            transitionDuration={300}
        >
            {/* Main content area that will grow and push user profile to bottom */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Main navigation */}
                <MobileNavigationContainer>
                    <List disablePadding>
                        {navigationItems.map((item) => (
                            <MobileNavigationItem
                                key={item.segment || item.title}
                                item={item}
                                onNavigate={onNavigate}
                                expandedItems={mobileExpandedItems}
                                onToggleExpand={handleMobileToggleExpand}
                            />
                        ))}
                    </List>
                </MobileNavigationContainer>

                {/* Site Selection and Configuration - push to middle/bottom area but above user profile */}
                <Box sx={{ marginTop: 'auto' }}>
                    {!isPureDesignUser && (
                        <MobileNavigationContainer>
                            <List disablePadding>
                                <MobileNavigationItem
                                    key={'site-selection'}
                                    item={{
                                        icon: <img src={Sitemap} alt="Configuration" style={{ width: 24, height: 24 }} />,
                                        title: _T('Site Selection'),
                                        fullPath: 'site-selection'
                                    }}
                                    onNavigate={onNavigate}
                                    expandedItems={mobileExpandedItems}
                                    onToggleExpand={handleMobileToggleExpand}
                                />
                            </List>
                        </MobileNavigationContainer>
                    )}

                    {!isPureDesignUser && configurationItem && (
                        <MobileNavigationContainer>
                            <List disablePadding>
                                <MobileNavigationItem
                                    key={configurationItem.segment || configurationItem.title}
                                    item={configurationItem}
                                    onNavigate={onNavigate}
                                    expandedItems={mobileExpandedItems}
                                    onToggleExpand={handleMobileToggleExpand}
                                />
                            </List>
                        </MobileNavigationContainer>
                    )}
                </Box>
            </Box>

            {/* User Profile Section - Always at the very bottom */}
            <MobileNavigationContainer sx={{ marginTop: 0 }}>
                <MobileUserProfileSection
                    hasAccessVrs={hasAccessVrs}
                    setShowEditProfileDialog={setShowEditProfileDialog}
                    setShowAboutDialog={setShowAboutDialog}
                    logOut={logOut}
                    userDispatch={userDispatch}
                    expanded={mobileExpandedItems['user-profile'] || false}
                    onToggleExpand={() => handleMobileToggleExpand('user-profile')}
                />
            </MobileNavigationContainer>
        </Drawer>
    );
}
